/**
 * Disabled Reasons Service
 * 
 * Manages disabled reasons for account deactivation.
 * Stores reasons in Firestore 'disabledReasons' collection.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  getDoc,
  deleteDoc,
  serverTimestamp,
  orderBy,
  query,
} from 'firebase/firestore';
import { firestore } from './firebase/config';

export interface DisabledReason {
  id: string;
  code: string; // Unique identifier (e.g., "payment_failed")
  title: string; // Display title (e.g., "Payment Method Failed")
  message: string; // User-facing message
  isActive: boolean; // Whether this reason is available for selection
  createdAt: any;
  updatedAt: any;
  order?: number; // Optional ordering for display
}

/**
 * Get all disabled reasons
 */
export const getDisabledReasons = async (): Promise<DisabledReason[]> => {
  const reasonsSnapshot = await getDocs(
    query(collection(firestore, 'disabledReasons'), orderBy('order', 'asc'), orderBy('title', 'asc'))
  );
  
  return reasonsSnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as DisabledReason[];
};

/**
 * Get active disabled reasons only
 */
export const getActiveDisabledReasons = async (): Promise<DisabledReason[]> => {
  const reasons = await getDisabledReasons();
  return reasons.filter(reason => reason.isActive);
};

/**
 * Get a single disabled reason by ID
 */
export const getDisabledReason = async (reasonId: string): Promise<DisabledReason | null> => {
  const reasonDoc = await getDoc(doc(firestore, 'disabledReasons', reasonId));
  if (!reasonDoc.exists()) {
    return null;
  }
  return {
    id: reasonDoc.id,
    ...reasonDoc.data(),
  } as DisabledReason;
};

/**
 * Get a disabled reason by code
 */
export const getDisabledReasonByCode = async (code: string): Promise<DisabledReason | null> => {
  const reasons = await getDisabledReasons();
  return reasons.find(reason => reason.code === code) || null;
};

/**
 * Create a new disabled reason
 */
export const createDisabledReason = async (
  code: string,
  title: string,
  message: string,
  isActive: boolean = true,
  order?: number
): Promise<string> => {
  // Check if code already exists
  const existing = await getDisabledReasonByCode(code);
  if (existing) {
    throw new Error(`A disabled reason with code "${code}" already exists`);
  }

  const reasonRef = await addDoc(collection(firestore, 'disabledReasons'), {
    code,
    title,
    message,
    isActive,
    order: order || 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  
  return reasonRef.id;
};

/**
 * Update a disabled reason
 */
export const updateDisabledReason = async (
  reasonId: string,
  updates: Partial<Omit<DisabledReason, 'id' | 'createdAt'>>
): Promise<void> => {
  // If updating code, check for duplicates
  if (updates.code) {
    const existing = await getDisabledReasonByCode(updates.code);
    if (existing && existing.id !== reasonId) {
      throw new Error(`A disabled reason with code "${updates.code}" already exists`);
    }
  }

  await updateDoc(doc(firestore, 'disabledReasons', reasonId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Delete a disabled reason
 */
export const deleteDisabledReason = async (reasonId: string): Promise<void> => {
  await deleteDoc(doc(firestore, 'disabledReasons', reasonId));
};


